import { ref, type Ref } from 'vue';
import { useProjectStore } from '@/stores/project.store';
import { debounce } from 'lodash';

interface UseProjectSelectOptions {
  customerId: Ref<any>;
  selectedId: Ref<any>;
  pageSize?: number;
  debounceMs?: number;
}

export function useProjectSelect(options: UseProjectSelectOptions) {
  const { customerId, selectedId, pageSize = 500, debounceMs = 300 } = options;
  const { getProjectsByCustomer } = useProjectStore();

  const projects = ref<any[]>([]);
  const loading = ref(false);
  const hasMore = ref(false);
  const nextPage = ref<number | null>(null);
  const lastSearch = ref('');

  async function ensureSelectedInList() {
    const id = selectedId.value;
    if (id == null) return;
    const numericId = typeof id === 'object' ? (id as any).id : id;
    if (numericId == null) return;
    const found = projects.value.some(
      (p: any) => p.id === numericId || String(p.id) === String(numericId)
    );
    if (found) return;
    try {
      // Fetch with name filter won't help — just fetch all pages isn't practical.
      // Instead, fetch the specific project by filtering on its ID.
      const response = await getProjectsByCustomer({
        customer: customerId.value,
        project: numericId,
        is_active: true,
      });
      if (response.results?.length) {
        projects.value = [...response.results, ...projects.value];
      }
    } catch {
      // Project might have been deleted
    }
  }

  async function fetchProjects(search?: string) {
    if (!customerId.value) return;
    loading.value = true;
    lastSearch.value = search || '';
    try {
      const response = await getProjectsByCustomer({
        page_size: pageSize,
        customer: customerId.value,
        is_active: true,
        search: search || undefined,
      });
      projects.value = [...response.results];
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
      if (!search) await ensureSelectedInList();
    } finally {
      loading.value = false;
    }
  }

  async function loadMore() {
    if (!nextPage.value || loading.value || !customerId.value) return;
    loading.value = true;
    try {
      const response = await getProjectsByCustomer({
        page_size: pageSize,
        page: nextPage.value,
        customer: customerId.value,
        is_active: true,
        search: lastSearch.value || undefined,
      });
      projects.value = [...projects.value, ...response.results];
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
    } finally {
      loading.value = false;
    }
  }

  const onSearch = debounce((query: string) => {
    fetchProjects(query);
  }, debounceMs);

  return {
    projects,
    loading,
    hasMore,
    fetchProjects,
    loadMore,
    onSearch,
  };
}
